import AtlasViewport from "./AtlasViewport";
import { Point2d } from "../../../common/geometry/Geom2d";
import IPositionalElement from "../../../common/elements/geom/IPositionalElement";

export default abstract class AtlasElement implements IPositionalElement {
    protected _x: number | (() => number);
    protected _y: number | (() => number);

    protected _onElementMove?: ((newPos: Point2d) => void);

    constructor(args?: {
        x?: number | (() => number);
        y?: number | (() => number);
        sortIndex?: number;
        onElementMove?: ((newPos: Point2d) => void);
    }) {
        this._x = args?.x ?? 0;
        this._y = args?.y ?? 0;

        this._onElementMove = args?.onElementMove;
    }

    public get x(): number {
        if (typeof this._x === "number") {
            return this._x;
        } else {
            return this._x();
        }
    }
    public set x(val: number | (() => number)) {
        this._x = val;
    }

    public get y(): number {
        if (typeof this._y === "number") {
            return this._y;
        } else {
            return this._y();
        }
    }
    public set y(val: number | (() => number)) {
        this._y = val;

        this._onElementMove?.({x: this.x, y: this.y});
    }

    public get position(): Point2d {
        return { x: this.x, y: this.y };
    }

    public set position(val: Point2d) {
        this.x = val.x;
        this.y = val.y;

        this._onElementMove?.({x: this.x, y: this.y});
    }

    public set onElementMove(func: ((newPos: Point2d) => void) | null) {
        this._onElementMove = func ?? undefined;
    }

    /** Adjusts the position of the element by the given axial amounts. Returns the new x- and y-coordinates of the element */
    public translate(x: number, y: number): Point2d {
        if (typeof this._x !== "number" || typeof this._y !== "number") {
            console.error(
                "Attempting to translate an Atlas Element with functionally-determined coordinates"
            );
            return this.position;
        } else {
            const newX = this._x + x;
            const newY = this._y + y;

            this._x = newX;
            this._y = newY;

            this._onElementMove?.({x: newX, y: newY})

            return {x: newX, y: newY}
        }
    }

    /** Render the atlas element. Gets called every animation frame, so long as the element is not being culled. */
    public abstract draw(
        ctx: CanvasRenderingContext2D,
        viewport: AtlasViewport
    ): void;
}
